import { useContext, useEffect, useState } from "react";
import { LanguageContext } from "../../../context/LanguageContext";
import { InnerWrapper, MainContainer, TitleAndActionBox } from "../../common";
import { Grid } from "@mui/material";
import { useAuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "../../../context/SnackbarContext";
import { handleDeleteBusiness } from "../../common/DeleteBusiness";
import { Sync } from "../../../SyncService";
import { handleApi } from "../../../apis";
import { POST_UPLOAD_RECEIPT } from "../../../apis/configuration";
import {
  getBusinessCount,
  updateBusinessImageUrl,
  deleteBusinessImageUrl,
} from "../../../database/helpers/business";
import ConfirmationModal from "../../common/Modals/ConfirmationModal";
import ProfileCard from "./ProfileCard";
import styles from "./styles.module.css";

function Profile() {
  const [I18n] = useContext(LanguageContext);
  const { business, user, refreshBusiness } = useAuthContext();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState(business?.image_url || "");
  const [isImageLoading, setIsImageLoading] = useState(false);

  useEffect(() => {
    setImageUrl(business?.image_url || "");
  }, [business?.id]);

  const openDeleteModal = async () => {
    const count = await getBusinessCount();
    setCanDelete(count > 1);
    setDeleteModalOpen(true);
  };

  const handleUploadAvatar = async (base64) => {
    try {
      setIsImageLoading(true);
      const base64Data = base64.split(",");
      const formData = new FormData();
      formData.append("transaction_id", business.id);
      formData.append("image_type", "jpeg");
      formData.append("receipt", base64Data[1]);

      const uploadResponse = await handleApi(
        POST_UPLOAD_RECEIPT,
        formData,
        null,
        navigate,
        { "Content-Type": "multipart/form-data" },
      );

      const uploadedUrl = uploadResponse?.url;
      if (!uploadedUrl) throw new Error("No URL returned from upload");

      setImageUrl(uploadedUrl);
      await updateBusinessImageUrl(business, uploadedUrl);
      refreshBusiness();
      Sync(user.phone_number, navigate);
    } catch (e) {
      showSnackbar({ message: I18n.something_went_wrong, severity: "error" });
    } finally {
      setIsImageLoading(false);
    }
  };

  const handleDeleteAvatar = async () => {
    setDeleteImageModalOpen(false);
    try {
      setImageUrl("");
      await deleteBusinessImageUrl(business);
      refreshBusiness();
      Sync(user.phone_number, navigate);
    } catch (e) {
      showSnackbar({ message: I18n.something_went_wrong, severity: "error" });
    }
  };

  const handleDelete = () => {
    setDeleteModalOpen(false);
    handleDeleteBusiness(
      (msg) => showSnackbar({ message: msg, severity: "success" }),
      () => {},
      true,
      setDeleteLoading,
      business,
      navigate,
      user,
      I18n,
    );
  };

  return (
    <MainContainer fluid className={`quickSalePage ${styles.profileScreen}`}>
      <Grid container>
        <Grid item md={12}>
          <TitleAndActionBox
            data={{
              title: I18n.profile,
            }}
          ></TitleAndActionBox>

          <InnerWrapper className="innerWrapper newInnerWrapper">
            <ProfileCard
              business={business}
              user={user}
              imageUrl={imageUrl}
              onEditProfile={() => navigate("/Profile/Edit", { state: { isEdit: true } })}
              onDeleteBusiness={openDeleteModal}
              onUploadAvatar={handleUploadAvatar}
              onDeleteAvatar={() => setDeleteImageModalOpen(true)}
              isImageLoading={isImageLoading}
            />
          </InnerWrapper>
        </Grid>
      </Grid>

      <ConfirmationModal
        open={deleteImageModalOpen}
        onClose={() => setDeleteImageModalOpen(false)}
        onConfirm={handleDeleteAvatar}
        message={I18n.are_you_sure_you_want_to_delete_image}
        confirmText={I18n.delete}
        confirmButtonClassName="ubDeleteButton"
        id="profile_delete_image_modal"
      />

      <ConfirmationModal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        confirmButtonClassName={
          canDelete ? "ubDeleteButton" : styles.hideConfirmButton
        }
        onConfirm={canDelete ? handleDelete : () => setDeleteModalOpen(false)}
        message={
          canDelete
            ? I18n.are_you_sure_you_want_to_delete_business
            : "You must have at least 1 business"
        }
        confirmText={canDelete ? I18n.confirm : ""}
        cancelText={canDelete ? undefined : I18n.cancel}
        loading={canDelete ? deleteLoading : false}
        id="profile_delete_business_modal"
        customClassName={styles.deleteBusinessModal}
      />
    </MainContainer>
  );
}

export default Profile;
